import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { APP_CONFIG } from '@application/config';
import { Observable } from "rxjs";
import { TComment, TProfile, TPhone, TResponsible, TAddress, IPersonWithIncludesParams, EPersonIncludeType, TCommentToggleRead, TTimelineEvent, TGraduationList, TGraduation, TPersonIntegration, TPaguelaAccount, TActivity } from "../entities/profile.model";
import { TAccessLog } from "../profile.store";
import { ELogicalDirectionEnum, TCategory, TCategoryHistory } from "@settings/data-access";
import { CreditAssessmentPF, CreditAssessmentPFAll, CreditAssessmentPJ, CreditAssessmentPJAll } from "../entities/credit-assessment.model";
import { TPersonProduct } from "@consumption/data-access";
import { TPersonSearch } from '@shared/components/navbar/data-access';
import { EPaguelaSituation } from '@financial/data-access';

@Injectable({ providedIn: 'root' })
export class ProfileService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(APP_CONFIG).apiUrl;

  public getProfile(id: string): Observable<TProfile> {
    return this.http.get<TProfile>(`${this.apiUrl.personService}/Person`,
      {
        params: { id },
        withCredentials: true
      }
    );
  }

  public checkAccessCdExists(cd: number): Observable<boolean> {
    return this.http.get<boolean>(`${this.apiUrl.personService}/Person/IsAccessPinAlreadyInUse?accessPin=${cd}`, {
      withCredentials: true
    });
  }
  public searchPerson<T>(query: string): Observable<TPersonSearch> {
    return this.http.get<TPersonSearch>(`${this.apiUrl.search_service}/personsearch/search`, {
      params: {
        q: query,
      },
      withCredentials: true
    });
  }

  public checkFiscalIdentity<T>(type: 'cnpj' | 'cpf', value: string): Observable<T> {
    return this.http.get<T>(`${this.apiUrl.personService}/PublicData/${type}/${value}`, {
      withCredentials: true
    });
  }

  public createPerson(person: TProfile): Observable<TProfile> {
    return this.http.post<TProfile>(`${this.apiUrl.personService}/Person`, person, {
      withCredentials: true
    });
  }

  public updatePerson(person: TProfile): Observable<TProfile> {
    return this.http.put<TProfile>(`${this.apiUrl.personService}/Person`, person, {
      withCredentials: true
    });
  }

  public patchPerson(propertyPatch: Partial<TProfile>): Observable<TProfile> {
    return this.http.patch<TProfile>(`${this.apiUrl.personService}/Person`, propertyPatch, {
      withCredentials: true
    });
  }

  public getResponsible(personId: string): Observable<TResponsible> {
    return this.http.get<TResponsible>(`${this.apiUrl.personService}/Responsible`, {
      params: { personId },
      withCredentials: true
    });
  }

  public createResponsible(responsible: TResponsible): Observable<TResponsible> {
    return this.http.post<TResponsible>(`${this.apiUrl.personService}/Responsible`, responsible, {
      withCredentials: true
    });
  }

  public updateResponsible(responsible: TResponsible): Observable<TResponsible> {
    return this.http.put<TResponsible>(`${this.apiUrl.personService}/Responsible`, responsible, {
      withCredentials: true
    });
  }

  public patchResponsible(propertyPatch: Partial<TResponsible>): Observable<TResponsible> {
    return this.http.patch<TResponsible>(`${this.apiUrl.personService}/Responsible`, propertyPatch, {
      withCredentials: true
    });
  }

  public deleteResponsible(personId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl.personService}/Responsible`, {
      params: { personId },
      withCredentials: true
    });
  }

  public getAccessFrequency(personId: string, startOn: string, endOn: string): Observable<{ "@odata.context": string; value: TAccessLog[] }> {
    return this.http.get<{ "@odata.context": string; value: TAccessLog[] }>(`${this.apiUrl.odata_access_log_service}/AccessLogs`, {
      params: {
        $orderby: 'AccessOnUtc',
        $select: 'AccessOnUtc,AccessLogId',
        $filter: `(AccessDirection eq ${ELogicalDirectionEnum.Entry}) and (IsAccessGranted eq true) and (AccessOnUtc ge ${startOn}) and (AccessOnUtc le ${endOn}) and (PersonId eq ${personId})`,
      },
      withCredentials: true
    });
  }

  public getComment(id: string): Observable<TComment> {
    return this.http.get<TComment>(`${this.apiUrl.personService}/Comment`, {
      withCredentials: true,
      params: {
        id
      }
    });
  }

  public toggleReadComment(commentId: string): Observable<TCommentToggleRead> {
    return this.http.get<TCommentToggleRead>(`${this.apiUrl.personService}/Comment/ToggleRead`, {
      withCredentials: true,
      params: {
        commentId
      }
    });
  }

  public adicionarComentario(comment: unknown): Observable<unknown> {
    return this.http.post<unknown>(`${this.apiUrl.personService}/Comment`, comment, {
      withCredentials: true
    });
  }

  public atualizarComentario(comment: unknown): Observable<unknown> {
    return this.http.put<unknown>(`${this.apiUrl.personService}/Comment`, comment, {
      withCredentials: true
    });
  }

  public removerComentario(id: string): Observable<unknown> {
    return this.http.delete<unknown>(`${this.apiUrl.personService}/Comment`, {
      withCredentials: true,
      params: { id }
    });
  }

  public getLastComment(personId: string): Observable<{ "@odata.context": string; value: TComment[] }> {
    return this.http
      .get<{ "@odata.context": string; value: TComment[] }>(`${this.apiUrl.odata_person_service}/Comments`, {
        params: {
          $filter: `TargetPersonId eq ${personId} and Read eq false`,
          skip: '0',
          $top: '1',
          $orderby: 'AddedOn desc',
        },
        withCredentials: true
      })
  }

  public getByPerson(personId: string): Observable<TPhone[]> {
    return this.http.get<TPhone[]>(`${this.apiUrl.personService}/Phone/GetByPerson`, {
      params: { personId },
      withCredentials: true
    });
  }

  public getPhone(id: string): Observable<TPhone> {
    return this.http.get<TPhone>(`${this.apiUrl.personService}/Phone`, {
      params: { id },
      withCredentials: true
    });
  }

  public createPhone(phone: TPhone): Observable<TPhone> {
    return this.http.post<TPhone>(`${this.apiUrl.personService}/Phone`, phone, {
      withCredentials: true
    });
  }

  public updatePhone(phone: TPhone): Observable<TPhone> {
    return this.http.put<TPhone>(`${this.apiUrl.personService}/Phone`, phone, {
      withCredentials: true
    });
  }

  public patchPhone(propertyPatch: Partial<TPhone>): Observable<TPhone> {
    return this.http.patch<TPhone>(`${this.apiUrl.personService}/Phone`, propertyPatch, {
      withCredentials: true
    });
  }

  public deletePhone(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl.personService}/Phone`, {
      params: { id },
      withCredentials: true
    });
  }

  public getAddressByPerson(personId: string): Observable<TAddress[]> {
    return this.http.get<TAddress[]>(`${this.apiUrl.personService}/Address/GetByPerson`, {
      params: { personId },
      withCredentials: true
    });
  }

  public getAddress(id: string): Observable<TAddress> {
    return this.http.get<TAddress>(`${this.apiUrl.personService}/Address`, {
      params: { id },
      withCredentials: true
    });
  }

  public createAddress(address: TAddress): Observable<TAddress> {
    return this.http.post<TAddress>(`${this.apiUrl.personService}/Address`, address, {
      withCredentials: true
    });
  }

  public updateAddress(address: TAddress): Observable<TAddress> {
    return this.http.put<TAddress>(`${this.apiUrl.personService}/Address`, address, {
      withCredentials: true
    });
  }

  public patchAddress(propertyPatch: Partial<TAddress>): Observable<TAddress> {
    return this.http.patch<TAddress>(`${this.apiUrl.personService}/Address`, propertyPatch, {
      withCredentials: true
    });
  }

  public deleteAddress(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl.personService}/Address`, {
      params: { id },
      withCredentials: true
    });
  }

  public getPersonWithIncludes(id: string): Observable<TProfile> {
    return this.http.get<TProfile>(`${this.apiUrl.personService}/PersonWithIncludes`, {
      params: { id },
      withCredentials: true
    });
  }

  public createPersonWithIncludes(person: TProfile): Observable<TProfile> {
    return this.http.post<TProfile>(`${this.apiUrl.personService}/PersonWithIncludes`, person, {
      withCredentials: true
    });
  }

  public updatePersonWithIncludes(person: TProfile): Observable<TProfile> {
    return this.http.put<TProfile>(`${this.apiUrl.personService}/PersonWithIncludes`, person, {
      withCredentials: true
    });
  }

  public patchPersonWithIncludes(propertyPatch: Partial<TProfile>): Observable<TProfile> {
    return this.http.patch<TProfile>(`${this.apiUrl.personService}/PersonWithIncludes`, propertyPatch, {
      withCredentials: true
    });
  }

  public deletePersonWithIncludes(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl.personService}/PersonWithIncludes`, {
      params: { id },
      withCredentials: true
    });
  }

  public getPersonWithIncludesPost(params: IPersonWithIncludesParams): Observable<TProfile> {
    return this.http.post<TProfile>(
      `${this.apiUrl.personService}/PersonWithIncludes/GetPersonWithIncludes`,
      params,
      { withCredentials: true }
    );
  }

  /**
   * Helper method to fetch person with specific includes
   * @param personId Person ID
   * @param includeTypes Types of data to be included in the response
   * @returns Observable with person data and requested includes
   */
  public getPersonWithSpecificIncludes(
    personId: string,
    includeTypes: EPersonIncludeType[] = [],
  ): Observable<TProfile> {
    return this.getPersonWithIncludesPost({
      personId,
      includes: includeTypes,
    });
  }


  public getTotalAccountsReceivablesSummaryByPersonId(personId: string): Observable<number> {
    return this.http
      .get<number>(`${this.apiUrl.receivable_service}/AccountReceivable/GetTotalAccountsReceivablesSummaryByPersonId`, {
        params: { personId },
        withCredentials: true
      });
  }

  public getTotalReceivementDailySummaryByPersonId(personId: string): Observable<number> {
    return this.http
      .get<number>(`${this.apiUrl.receivable_service}/Receivement/GetTotalReceivementDailySummaryByPersonId`, {
        params: { personId },
        withCredentials: true,
      });
  }

  public getTotalOpenMonthByPersonId(personId: string): Observable<number> {
    return this.http
      .get<number>(`${this.apiUrl.receivable_service}/AccountReceivable/GetTotalOpenMonthByPerson`, {
        params: { personId },
        withCredentials: true
      });
  }

  public getTimelineByPeriod(personId: string, yearMonth: string): Observable<{ NextDate: string; List: TTimelineEvent[] }> {
    return this.http.get<{ NextDate: string; List: TTimelineEvent[] }>(`${this.apiUrl.personService}/Timeline`, {
      params: {
        personId,
        yearMonth
      },
      withCredentials: true
    });
  }

  public getAllGraduationsByPersonId(personId: string): Observable<TGraduationList[]> {
    return this.http.get<TGraduationList[]>(`${this.apiUrl.graduation_service}/PersonActivityLevel/GetLastGraduationsByPerson`, {
      params: { personId },
      withCredentials: true
    });
  }

  public getGraduationById(id: string): Observable<TGraduation> {
    return this.http.get<TGraduation>(`${this.apiUrl.graduation_service}/PersonActivityLevel`, {
      params: { id },
      withCredentials: true
    });
  }

  public deleteGraduationById(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl.graduation_service}/PersonActivityLevel`, {
      params: { id },
      withCredentials: true
    });
  }

  public createGraduation(graduation: Omit<TGraduation, 'PersonActivityLevelId'>): Observable<TGraduation> {
    return this.http.post<TGraduation>(`${this.apiUrl.graduation_service}/PersonActivityLevel`, graduation, {
      withCredentials: true
    });
  }

  public updateGraduation(graduation: TGraduation): Observable<TGraduation> {
    return this.http.put<TGraduation>(`${this.apiUrl.graduation_service}/PersonActivityLevel`, graduation, {
      withCredentials: true
    });
  }

  public getContractInfoByPersonId(personId: string): Observable<string> {
    return this.http.get(`${this.apiUrl.contract}/Contracts/GetInfoByPersonId?personId=${personId}`, {
      withCredentials: true,
      responseType: 'text',
    });
  }

  public copyToPaguela(personId: string): Observable<TProfile> {
    return this.http
      .post<TProfile>(`${this.apiUrl.personService}/Person/CopyAccountActuarToPagueLa`, null, {
        params: { personId },
        withCredentials: true
      });
  }

  public generateSimpleLoginCode(personId: string): Observable<any> {
    return this.http
      .post(`${this.apiUrl.userService}/Auth/GenerateSimpleLoginCode?personId=${personId}`, {},
        {
          withCredentials: true
        }
      );
  }
  
  public getPersonCategoryHistoryByPersonId(personId: string): Observable<TCategoryHistory[]> {
    return this.http
      .get<TCategoryHistory[]>(`${this.apiUrl.personService}/FinancialCategory/GetHistoriesByPerson`, {
        params: { personId },
        withCredentials: true
      });
  }

  public getPersonCategoryByPersonId(personId: string): Observable<TCategory> {
    return this.http
      .get<TCategory>(`${this.apiUrl.personService}/FinancialCategory/GetLastByPerson`, {
        params: { personId },
        withCredentials: true
      });
  }

  public getCreditAssessmentPJ(cnpj: string): Observable<CreditAssessmentPJ> {
    return this.http.get<CreditAssessmentPJ>(
      `${this.apiUrl.credit_assessment_service}/SerasaPJ/GetSerasaPJ`,
      {
        params: { cnpj },
        withCredentials: true
      }
    );
  }

  public getLastCreditAssessmentPJ(cnpj: string): Observable<CreditAssessmentPJ> {
    return this.http.get<CreditAssessmentPJ>(
      `${this.apiUrl.credit_assessment_service}/SerasaPJ/GetSerasaPJLastAdded`,
      {
        params: { cnpj },
        withCredentials: true
      }
    );
  }

  public getAllCreditAssessmentPJ(cnpj: string): Observable<CreditAssessmentPJAll[]> {
    return this.http.get<CreditAssessmentPJAll[]>(
      `${this.apiUrl.credit_assessment_service}/SerasaPJ/GetSerasaPJAllAdded`,
      {
        params: { cnpj },
        withCredentials: true,
      }
    );
  }

  public getSerasaPJById(id: string): Observable<CreditAssessmentPJ> {
    return this.http.get<CreditAssessmentPJ>(
      `${this.apiUrl.credit_assessment_service}/SerasaPJ/GetSerasaPJById`,
      {
        params: { id },
        withCredentials: true
      }
    );
  }

  public getCreditAssessmentPF(cpf: string): Observable<CreditAssessmentPF> {
    return this.http.get<CreditAssessmentPF>(
      `${this.apiUrl.credit_assessment_service}/SerasaPF/GetSerasaPF`,
      {
        params: { cpf },
        withCredentials: true
      }
    );
  }

  public getLastCreditAssessmentPF(cpf: string): Observable<CreditAssessmentPF> {
    return this.http.get<CreditAssessmentPF>(
      `${this.apiUrl.credit_assessment_service}/SerasaPF/GetSerasaPFLastAdded`,
      {
        params: { cpf },
        withCredentials: true
      }
    );
  }

  public getAllCreditAssessmentPF(cpf: string): Observable<CreditAssessmentPFAll[]> {
    return this.http.get<CreditAssessmentPFAll[]>(
      `${this.apiUrl.credit_assessment_service}/SerasaPF/GetSerasaPFAllAdded`,
      {
        params: { cpf },
        withCredentials: true,
      }
    );
  }

  public getSerasaPFById(id: string): Observable<CreditAssessmentPF> {
    return this.http.get<CreditAssessmentPF>(
      `${this.apiUrl.credit_assessment_service}/SerasaPF/GetSerasaPFById`,
      {
        params: { id },
        withCredentials: true
      }
    );
  }

  public getIntegration(id: string): Observable<TPersonIntegration> {
    return this.http.get<TPersonIntegration>(`${this.apiUrl.personService}/Integration`, {
      params: { id },
      withCredentials: true
    });
  }

  public updateIntegration(data: TPersonIntegration): Observable<TPersonIntegration> {
    return this.http.patch<TPersonIntegration>(`${this.apiUrl.personService}/Integration`, data , {
      withCredentials: true
    });
  }

  public checkTotalPassKey(id: string): Observable<any> {
    return this.http.get<any>(`${this.apiUrl.totalpass}/Gym/VerifyCredentialAsync?gymId=${id}`, {
      withCredentials: true
    });
  }

  public getPersonProducts(personId: string): Observable<TPersonProduct[]> {
    return this.http.get<TPersonProduct[]>(`${this.apiUrl.subscription_service}/Person/Products/activity`, {
      params: {
        personId
      },
      withCredentials: true,
    });
  }
  
  public getPaguelaAccount(id: string): Observable<TPaguelaAccount> {
    return this.http.get<TPaguelaAccount>(`${this.apiUrl.paguela}/Contas/ObterPorId`, {
      params: {
        id,
      },
      withCredentials: true
    });
  }

  public updatePaguelaSituation(id: string, situacao: EPaguelaSituation): Observable<TPaguelaAccount> {
    return this.http.put<TPaguelaAccount>(`${this.apiUrl.paguela}/Contas/AtualizarSituacao`, null, {
      params: {
        id,
        situacao,
      },
      withCredentials: true
    });
  }

    public getActivitiesByPersonId(personId?: string): Observable<{ "@odata.context": string; value: TActivity[] }> {
        return this.http.get<{ "@odata.context": string; value: TActivity[] }>(
        `${this.apiUrl.odata_activity_service}/PersonsWorkers`,
        {
            params: {
            $filter: `PersonId eq ${personId}`,

            },
            withCredentials: true
        }
        );
    }

    public getAllActivities(): Observable<{ value: TActivity[] }> {
        return this.http.get<{ value: TActivity[] }>(`${this.apiUrl.odata_activity_service}/Activities`, {
            params: {
                $select: 'Description,ActivityId,Icon',
                $orderby: 'Description',
            },
            withCredentials: true
        });
    }
}
